"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";
import { useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import useGetCampaignStatistics from "@/hooks/requests/campaign-statistics/use-get-campaign-statistics";
import usePagination from "@/hooks/use-pagination";
import { StatisticsPageMessages } from "@/messages/campaigns/statistics/statistics-page.messages";

import { CampaignTable } from "./components/Table";

type Props = {
  messages: StatisticsPageMessages;
};

function CampaignStatisticsPage({ messages }: Props) {
  const { campaignId } = useParams<{ campaignId: string }>();
  const pagination = usePagination();
  const { pageNumber, pageSize } = pagination;

  const getCampaignStatistic = useGetCampaignStatistics({ campaignId, pageNumber, pageSize });
  const { data, isLoading, error } = getCampaignStatistic;

  const hasData = !!data && data.items.length > 0;
  const noData = !!data && data.items.length === 0;

  const shouldLoadTable = hasData && !isLoading && !error;
  const shouldLoadError = !isLoading && !!error;
  const shouldLoadNoData = noData || typeof data === "string";

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Card>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={50} />}>
            <MDBox p={2}>
              <MDBox p={2}>
                <MDTypography variant="h5">{messages.pageTitle}</MDTypography>
              </MDBox>

              {shouldLoadTable && (
                <CampaignTable
                  campaignId={campaignId}
                  data={data}
                  messages={messages}
                  pagination={pagination}
                />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.statisticsErrorToLoad}
                  description={error.message}
                  alt={messages.statisticsErrorAlt}
                />
              )}

              {shouldLoadNoData && (
                <AlertBlock
                  title={messages.statisticsNotFoundError}
                  description={messages.statisticsNotFoundDescription}
                  alt={messages.statisticsNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default CampaignStatisticsPage;
